import { useEffect } from "react";
import { SaveOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Card, Col, Form, Input, message, Row, Select, Space, Spin, Typography } from "antd";

import { AppConfigType, type AppVersionConfig } from "@fitness-app/data-models/entities/TrainerConfig";
import { type Json } from "@fitness-app/supabase";

import { CurrentOfferCard } from "~/modules/AppConfig/currentOffer/CurrentOfferCard";
import { FreeMonthOffersCard } from "~/modules/AppConfig/FreeMonthOffersCard";
import { SalesCallCard } from "~/modules/AppConfig/SalesCallCard";
import { TraineeAiReportModelCard } from "~/modules/AppConfig/TraineeAiReportModelCard";
import { supabase } from "~/store/initializeStore";

import {
  isMobilePlatform,
  mapConfigToForm,
  mapFormToConfig,
  platforms,
  updateTypeForVersion,
  type AppConfigFormValue,
} from "./appVersionForm";

export default function AppConfig() {
  const [form] = Form.useForm<AppConfigFormValue>();
  const queryClient = useQueryClient();

  const configQuery = useQuery({
    queryKey: ["app_config", AppConfigType.APP_VERSION],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("app_config")
        .select()
        .eq("type", AppConfigType.APP_VERSION)
        .single<AppVersionConfig>();

      if (error) {
        throw error;
      }

      return data;
    },
  });

  useEffect(() => {
    if (configQuery.data) {
      form.setFieldsValue(mapConfigToForm(configQuery.data));
    }
  }, [configQuery.data, form]);

  const updateConfigMutation = useMutation({
    mutationFn: async (values: AppConfigFormValue) => {
      if (!configQuery.data?.id) {
        throw new Error("Brak rekordu konfiguracji aplikacji.");
      }

      const payload = mapFormToConfig(values, configQuery.data.data);

      const { data, error } = await supabase
        .from("app_config")
        .update({
          data: payload.data as unknown as Json,
          updatedAt: new Date().toISOString(),
          version: (configQuery.data.version ?? 0) + 1,
        })
        .eq("id", configQuery.data.id)
        .select()
        .single<AppVersionConfig>();

      if (error) {
        throw error;
      }

      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["app_config", AppConfigType.APP_VERSION], data);
      form.setFieldsValue(mapConfigToForm(data));
      void message.success("Konfiguracja aplikacji zostala zapisana.");
    },
    onError: (error) => {
      void message.error(error instanceof Error ? error.message : "Nie udalo sie zapisac konfiguracji.");
    },
  });

  if (configQuery.isLoading) {
    return (
      <div className="flex min-h-[360px] items-center justify-center">
        <Spin />
      </div>
    );
  }

  if (configQuery.isError) {
    return (
      <Alert
        type="error"
        showIcon
        message="Nie udalo sie pobrac konfiguracji aplikacji."
        description={configQuery.error instanceof Error ? configQuery.error.message : undefined}
      />
    );
  }

  return (
    <Row className="p-0 lg:p-6">
      <Col xs={24}>
        <TraineeAiReportModelCard />
        <FreeMonthOffersCard />
        <SalesCallCard />
        <CurrentOfferCard />
        <Card
          className="shadow"
          title="Konfiguracja aplikacji"
          extra={<Typography.Text type="secondary">Wersja rekordu: {configQuery.data?.version ?? "-"}</Typography.Text>}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={(values) => updateConfigMutation.mutate(values)}
            onValuesChange={(changed: Partial<AppConfigFormValue>) => {
              for (const { key } of platforms) {
                if (isMobilePlatform(key) && changed[key]?.current !== undefined) {
                  form.setFieldValue(
                    [key, "updateType"],
                    updateTypeForVersion(changed[key]!.current, configQuery.data?.data[key]),
                  );
                }
              }
            }}
          >
            <Space direction="vertical" size="large" className="w-full">
              <div>
                <Typography.Title level={4}>Wersje aplikacji</Typography.Title>
                <Row gutter={[16, 16]}>
                  {platforms.map((platform) => (
                    <Col xs={24} lg={8} key={platform.key}>
                      <Card size="small" title={platform.label}>
                        <Form.Item
                          name={[platform.key, "current"]}
                          label="Aktualna wersja"
                          rules={[{ required: true, message: "Podaj aktualna wersje." }]}
                        >
                          <Input />
                        </Form.Item>
                        {isMobilePlatform(platform.key) && (
                          <Form.Item
                            name={[platform.key, "updateType"]}
                            label="Typ aktualizacji"
                            dependencies={[[platform.key, "current"]]}
                            extra="Dotyczy tego wydania. Starsza wersja aplikacji może nadal wymagać aktualizacji ze sklepu."
                            rules={[
                              ({ getFieldValue }) => ({
                                validator: (_, value) => {
                                  const current = String(getFieldValue([platform.key, "current"]) ?? "").trim();
                                  return current !== configQuery.data?.data[platform.key]?.current.trim() && !value
                                    ? Promise.reject(new Error("Wybierz typ aktualizacji dla nowej wersji."))
                                    : Promise.resolve();
                                },
                              }),
                            ]}
                          >
                            <Select
                              allowClear
                              placeholder="Nie określono"
                              options={[
                                { value: "ota", label: "OTA — aktualizacja w aplikacji" },
                                { value: "store", label: "Sklep — App Store / Google Play" },
                              ]}
                            />
                          </Form.Item>
                        )}
                        <Form.Item
                          name={[platform.key, "minRequired"]}
                          label="Minimalna wymagana wersja"
                          rules={[{ required: true, message: "Podaj minimalna wersje." }]}
                        >
                          <Input />
                        </Form.Item>
                        <Form.Item name={[platform.key, "versionsWithIssues"]} label="Wersje z problemami">
                          <Input.TextArea rows={4} placeholder="Jedna wersja na linie" />
                        </Form.Item>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </div>

              <div className="flex justify-end">
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={updateConfigMutation.isLoading}
                >
                  Zapisz
                </Button>
              </div>
            </Space>
          </Form>
        </Card>
      </Col>
    </Row>
  );
}
